<script setup>
import { carImage, frontHub, frontRim, rearHub, rearRim } from '../utils/carAssets'
import { DRIVE_SECONDS } from '../utils/timing'

// Wheel ellipses in % of the car image (1318 x 689 px). `squash` is the foreshortening: ellipse width / height.
const wheels = [
  { name: 'front', rim: frontRim, hub: frontHub, left: 43.47, top: 63.13, width: 13.28, height: 32.66, squash: 0.778 },
  { name: 'rear', rim: rearRim, hub: rearHub, left: 93.13, top: 57.98, width: 6.6, height: 27.07, squash: 0.466 },
]
</script>

<template>
  <div class="thx-car" :style="{ '--drive': `${DRIVE_SECONDS}s` }" aria-hidden="true">
    <div class="thx-car__shadow" />
    <div class="thx-car__bob">
      <img class="thx-car__body" :src="carImage" width="1318" height="689" alt="" />

      <div
        v-for="wheel in wheels"
        :key="wheel.name"
        class="thx-car__wheel"
        :class="`thx-car__wheel--${wheel.name}`"
        :style="{
          left: `${wheel.left}%`,
          top: `${wheel.top}%`,
          width: `${wheel.width}%`,
          height: `${wheel.height}%`,
          '--squash': wheel.squash,
        }"
      >
        <div class="thx-car__disc">
          <img class="thx-car__rim" :src="wheel.rim" alt="" />
          <img class="thx-car__hub" :src="wheel.hub" alt="" />
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.thx-car {
  /*
   * The car races in from the right, brakes hard with locked wheels, stands still for a moment and drives off to the left.
   * Wheel turns = distance / (2 * pi * tire radius): 70cqw at speed, then the locked wheels skid 10cqw, then 82cqw driving off.
   */
  --front-turns-in: -636deg;
  --front-turns-out: -1381deg;
  --rear-turns-in: -753deg;
  --rear-turns-out: -1635deg;

  position: absolute;
  z-index: 4;
  top: 9.5cqw;
  left: 0;
  width: 30cqw;
  aspect-ratio: 1318 / 689;
  animation: thx-car-drive var(--drive) linear both;
}

.thx-car__shadow {
  position: absolute;
  left: -2%;
  bottom: -4%;
  width: 104%;
  height: 22%;
  background: radial-gradient(ellipse at center, rgb(0 0 0 / 60%), transparent 70%);
  filter: blur(1cqw);
  rotate: -6deg;
}

/* A small suspension bounce while driving. */
.thx-car__bob {
  position: absolute;
  inset: 0;
  transform-origin: 50% 90%;
  animation:
    thx-car-bob 0.16s ease-in-out infinite alternate,
    thx-car-pitch var(--drive) linear both;
}

.thx-car__body {
  display: block;
  width: 100%;
  height: 100%;
}

.thx-car__wheel {
  position: absolute;
}

/* A circle that is squashed to the wheel's ellipse; the rim turns inside it before it is squashed. */
.thx-car__disc {
  position: absolute;
  top: 0;
  left: 50%;
  height: 100%;
  aspect-ratio: 1;
  translate: -50% 0;
  scale: var(--squash) 1;
}

.thx-car__rim,
.thx-car__hub {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
}

.thx-car__rim {
  animation: thx-car-wheel var(--drive) linear both;
}

.thx-car__wheel--front {
  --turns-in: var(--front-turns-in);
  --turns-out: var(--front-turns-out);
}

.thx-car__wheel--rear {
  --turns-in: var(--rear-turns-in);
  --turns-out: var(--rear-turns-out);
}

/* Fast and steady, a hard stop, standing still, then pulling away. */
@keyframes thx-car-drive {
  0% {
    translate: 100cqw 0;
    animation-timing-function: linear;
  }

  28% {
    translate: 45cqw 0;
    animation-timing-function: cubic-bezier(0.1, 0.8, 0.3, 1);
  }

  36%,
  62% {
    translate: 35cqw 0;
  }

  62% {
    animation-timing-function: cubic-bezier(0.55, 0, 0.9, 0.5);
  }

  100% {
    translate: -32cqw 0;
  }
}

/* The wheels turn with the car, lock when the brakes slam on and turn again when it pulls away. */
@keyframes thx-car-wheel {
  0% {
    rotate: 0deg;
    animation-timing-function: linear;
  }

  28%,
  62% {
    rotate: var(--turns-in);
  }

  62% {
    animation-timing-function: cubic-bezier(0.55, 0, 0.9, 0.5);
  }

  100% {
    rotate: var(--turns-out);
  }
}

/* The nose dips when braking, the body rocks back and squats when pulling away. */
@keyframes thx-car-pitch {
  0%,
  26% {
    rotate: 0deg;
  }

  31% {
    rotate: -1.8deg;
  }

  36% {
    rotate: -2.2deg;
  }

  42% {
    rotate: 0.8deg;
  }

  48% {
    rotate: -0.3deg;
  }

  54%,
  62% {
    rotate: 0deg;
  }

  72% {
    rotate: 1.3deg;
  }

  100% {
    rotate: 0.6deg;
  }
}

@keyframes thx-car-bob {
  to {
    translate: 0 -0.12cqw;
  }
}

@media (prefers-reduced-motion: reduce) {
  .thx-car {
    display: none;
  }
}
</style>
